'use client';

import { useEffect } from 'react';

import {
  handleListingCatalogNavigateEvent,
  handleListingCharacterNavigateEvent,
  handleListingFandomHrefNavigateEvent,
  handleListingFandomNavigateEvent,
  LISTING_CATALOG_NAVIGATE_EVENT,
  LISTING_CHARACTER_NAVIGATE_EVENT,
  LISTING_FANDOM_HREF_NAVIGATE_EVENT,
  LISTING_FANDOM_NAVIGATE_EVENT,
} from '@/features/fanfic-card/consts/LISTING_FANDOM_NAVIGATE_EVENT';

type ListingNavigateHandlers = {
  onFandomsChange: (fandoms: string[]) => void;
  onCharacterCardNavigate: (characterId: string, fandomId?: string) => void;
  onCatalogNavigate: (catalogHref: string, preventNavigation: () => void) => void;
  onFandomHrefNavigate: (
    href: string,
    fandomId: string | undefined,
    preventNavigation: () => void,
  ) => void;
};

/**
 * Soft listing navigation from breadcrumbs / cards / header via window events.
 */
export const useListingNavigateEventListeners = ({
  onFandomsChange,
  onCharacterCardNavigate,
  onCatalogNavigate,
  onFandomHrefNavigate,
}: ListingNavigateHandlers): void => {
  useEffect(() => {
    const handleFandomNavigate = (event: Event): void => {
      handleListingFandomNavigateEvent(event, onFandomsChange);
    };
    const handleCharacterNavigate = (event: Event): void => {
      handleListingCharacterNavigateEvent(event, onCharacterCardNavigate);
    };
    const handleCatalogNavigateEvent = (event: Event): void => {
      handleListingCatalogNavigateEvent(event, onCatalogNavigate);
    };
    const handleFandomHrefNavigateEvent = (event: Event): void => {
      handleListingFandomHrefNavigateEvent(event, onFandomHrefNavigate);
    };

    window.addEventListener(LISTING_FANDOM_NAVIGATE_EVENT, handleFandomNavigate);
    window.addEventListener(LISTING_CHARACTER_NAVIGATE_EVENT, handleCharacterNavigate);
    window.addEventListener(LISTING_CATALOG_NAVIGATE_EVENT, handleCatalogNavigateEvent);
    window.addEventListener(LISTING_FANDOM_HREF_NAVIGATE_EVENT, handleFandomHrefNavigateEvent);

    return () => {
      window.removeEventListener(LISTING_FANDOM_NAVIGATE_EVENT, handleFandomNavigate);
      window.removeEventListener(LISTING_CHARACTER_NAVIGATE_EVENT, handleCharacterNavigate);
      window.removeEventListener(LISTING_CATALOG_NAVIGATE_EVENT, handleCatalogNavigateEvent);
      window.removeEventListener(LISTING_FANDOM_HREF_NAVIGATE_EVENT, handleFandomHrefNavigateEvent);
    };
  }, [onFandomsChange, onCharacterCardNavigate, onCatalogNavigate, onFandomHrefNavigate]);
};
